/**
 * Playfield geometry and state-derived view facts: where the falling block is
 * drawn, which level/theme is on show, and when the stack counts as high.
 */
import { blockRow, landingRow, timeLeftFraction, type CheckpointResult, type GameState } from '@dropstack/core';

export interface FallingView {
  /** Quantized row (0 = top), in 1/stepsPerRow steps. */
  row: number;
  landingRow: number;
  /** 1 at spawn → 0 at lock. */
  timeLeft: number;
  /** In the lock delay: resting on the stack, about to lock. */
  resting: boolean;
}

/** The falling block at engine time `now`, or null outside the `falling` phase. */
export function fallingView(state: GameState, now: number): FallingView | null {
  if (state.phase !== 'falling' || !state.active) return null;
  const row = blockRow(state, now);
  const land = landingRow(state);
  return { row, landingRow: land, timeLeft: timeLeftFraction(state, now), resting: row >= land };
}

export interface BlockOffsetInput {
  /** Block row (float, 0 = top). */
  row: number;
  /** Row the block lands on. */
  landingRow: number;
  /** Height of one well row (px). */
  rowPx: number;
  /** Rendered block height (px); at least one row. */
  blockPx: number;
}

/**
 * Vertical offset (px from the top of the well) of the block's top edge.
 *
 * A block one row tall sits exactly in its row. A taller block (a stacked
 * fraction plus the instruction line on a short screen) starts flush with the
 * top of the well and still comes to rest exactly on the stack: its travel is
 * spread proportionally over the rows it falls, so every gravity step still
 * shows and the gap to the stack closes to zero at the landing row.
 */
export function blockOffsetPx({ row, landingRow, rowPx, blockPx }: BlockOffsetInput): number {
  if (blockPx <= rowPx + 0.5) return row * rowPx;
  const restTop = (landingRow + 1) * rowPx - blockPx;
  const fraction = landingRow > 0 ? Math.min(1, Math.max(0, row / landingRow)) : 1;
  return fraction * restTop;
}

/**
 * The level on show. A checkpoint changes `state.level` the moment its answer
 * resolves, but the new theme and level number appear with the level banner:
 * until then (and if the run ends first) the previous level stays on screen.
 */
export function displayLevel(state: GameState): number {
  const change: CheckpointResult | null = state.pendingLevelChange;
  return change && state.phase !== 'levelBanner' ? change.levelBefore : state.level;
}

/** The stack is high: the well rim glows softly and the danger tick may play (6+ of 10 rows). */
export function isDanger(state: Pick<GameState, 'bricks' | 'config'>): boolean {
  return state.bricks >= state.config.wellRows - 4;
}

/** Stack height as tension, 0 (empty) → 1 (game over), for audio.setIntensity and the background. */
export function stackIntensity(state: Pick<GameState, 'bricks' | 'config'>): number {
  const max = state.config.wellRows - 1;
  return max > 0 ? Math.min(1, Math.max(0, state.bricks / max)) : 0;
}

/** Session accuracy so far: correct / answered, or null before the first answer. */
export function sessionAccuracy(state: Pick<GameState, 'history'>): number | null {
  const n = state.history.length;
  if (n === 0) return null;
  return state.history.filter((r) => r.outcome === 'correct').length / n;
}
